function getSessionIdFromCookieOrStorage() {
  const cookieMatch = /(?:^|; )session_id=([^;]*)/.exec(document.cookie);
  const cookieSessionId = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]).trim() : '';

  if (cookieSessionId && cookieSessionId !== 'undefined' && cookieSessionId !== 'null') {
    return cookieSessionId;
  }

  const storageSessionId = localStorage.getItem('session_id');
  if (storageSessionId && storageSessionId !== 'undefined' && storageSessionId !== 'null') {
    return storageSessionId;
  }

  return null;
}

function normalizeViewerToken(rawToken: string | null) {
  if (!rawToken) {
    return null;
  }

  let token = `${rawToken}`.trim();
  if (!token || token === 'undefined' || token === 'null') {
    return null;
  }

  if (token.startsWith('"') && token.endsWith('"')) {
    token = token.slice(1, -1).trim();
  }

  if (/^Bearer\s+/i.test(token)) {
    token = token.replace(/^Bearer\s+/i, '').trim();
  }

  return token || null;
}

function getViewerTokenFromStorage() {
  try {
    const tokenFromSessionStorage = sessionStorage.getItem('session_viewer');
    const normalizedFromSessionStorage = normalizeViewerToken(tokenFromSessionStorage);

    if (normalizedFromSessionStorage) {
      return {
        rawToken: tokenFromSessionStorage,
        normalizedToken: normalizedFromSessionStorage,
        source: 'sessionStorage',
      };
    }
  } catch {
    // sessionStorage access can fail in strict browser privacy contexts.
  }

  try {
    const tokenFromLocalStorage = localStorage.getItem('session_viewer');
    const normalizedFromLocalStorage = normalizeViewerToken(tokenFromLocalStorage);

    if (normalizedFromLocalStorage) {
      return {
        rawToken: tokenFromLocalStorage,
        normalizedToken: normalizedFromLocalStorage,
        source: 'localStorage',
      };
    }
  } catch {
    // localStorage access can fail in strict browser privacy contexts.
  }

  return {
    rawToken: null,
    normalizedToken: null,
    source: 'none',
  };
}

export async function checkLogin() {
  const API_URL_ENV = process.env.API_URL;
  const API_URL = `${API_URL_ENV}/current-user-viewer`;
  const {
    rawToken: rawViewerToken,
    normalizedToken: viewer_token,
    source: viewerTokenSource,
  } = getViewerTokenFromStorage();

  const hadBearerPrefix = Boolean(rawViewerToken && /^Bearer\s+/i.test(rawViewerToken.trim()));
  const hadWrappedQuotes = Boolean(
    rawViewerToken && rawViewerToken.trim().startsWith('"') && rawViewerToken.trim().endsWith('"')
  );
  const maskedPrefix = viewer_token ? `${viewer_token.slice(0, 6)}...` : 'none';

  const cookieMatch = /(?:^|; )session_id=([^;]*)/.exec(document.cookie);
  const sessionIdFromCookie = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]).trim() : '';
  const sessionIdFromStorage = localStorage.getItem('session_id') || '';
  let sessionIdSource = 'none';
  if (sessionIdFromCookie) {
    sessionIdSource = 'cookie';
  } else if (sessionIdFromStorage) {
    sessionIdSource = 'localStorage';
  }

  // Validate that session_viewer token exists before making the request
  if (!viewer_token) {
    console.warn('checkLogin: session_viewer token not found in browser storage');
    return null;
  }

  // TEMP DEBUG: remove once invalid-token issue is solved in all environments.
  console.log('[AUTH-TEMP-DEBUG] /current-user-viewer request context', {
    rawTokenLength: rawViewerToken?.length || 0,
    normalizedTokenLength: viewer_token.length,
    maskedPrefix,
    hadBearerPrefix,
    hadWrappedQuotes,
    viewerTokenSource,
    sessionIdSource,
    hasSessionId: Boolean(sessionIdFromCookie || sessionIdFromStorage),
  });

  const myHeaders = new Headers();
  myHeaders.append('Content-Type', 'application/json');
  myHeaders.append('Authorization', `Bearer ${viewer_token}`);

  const session_id = getSessionIdFromCookieOrStorage();
  if (session_id) {
    myHeaders.append('x-api-key', session_id);
  }

  const options = {
    method: 'GET',
    headers: myHeaders,
    credentials: 'include' as RequestCredentials,
  };

  const BROWSER_URL = process.env.BROWSER_URL;

  try {
    const response = await fetch(API_URL, options);

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.warn('[AUTH-TEMP-DEBUG] /current-user-viewer failed', {
        status: response.status,
        statusText: response.statusText,
      });

      // console.log('response', response);

      if (response.status === 401) {
        showCenteredModal('Session Expired.', BROWSER_URL);
      } else {
        showCenteredModal('An unexpected error occurred.', BROWSER_URL);
      }
      return null;
    }
  } catch (error) {
    // console.log('error', error);
    showCenteredModal('Internal Error occurred', BROWSER_URL);
    return null;
  }
}

function showCenteredModal(message: string, redirectUrl?: string) {
  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.backgroundColor = 'rgba(0,0,0,0.6)';
  overlay.style.display = 'flex';
  overlay.style.justifyContent = 'center';
  overlay.style.alignItems = 'center';
  overlay.style.zIndex = '1000';

  const modal = document.createElement('div');
  modal.style.backgroundColor = '#f0f0f0';
  modal.style.padding = '30px 40px';
  modal.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
  modal.style.maxWidth = '400px';
  modal.style.width = '90%';
  modal.style.fontFamily = 'Arial, sans-serif';
  modal.style.textAlign = 'center';

  const title = document.createElement('h2');
  title.textContent = 'VIDAA - Image Viewer';
  title.style.fontSize = '18px';
  title.style.fontWeight = 'bold';
  title.style.marginBottom = '20px';
  title.style.color = '#2c3e50';
  modal.appendChild(title);

  const msg = document.createElement('p');
  msg.textContent = message + ' Please log in again and reselect the patient.';
  msg.style.fontSize = '18px';
  msg.style.marginBottom = '20px';
  msg.style.color = '#2c3e50';
  modal.appendChild(msg);

  const buttonContainer = document.createElement('div');
  buttonContainer.style.display = 'flex';
  buttonContainer.style.justifyContent = 'center';

  const button = document.createElement('button');
  button.textContent = 'LOGIN';
  button.style.padding = '10px 20px';
  button.style.fontSize = '16px';
  button.style.border = '1px solid #2c3e50';
  button.style.borderRadius = '5px';
  button.style.color = '#ffffff';
  button.style.backgroundColor = '#34495e';
  button.style.cursor = 'pointer';
  button.style.transition = 'all 0.3s ease';

  button.addEventListener('mouseenter', () => {
    button.style.backgroundColor = '#2c3e50';
  });

  button.addEventListener('mouseleave', () => {
    button.style.backgroundColor = '#34495e';
  });

  button.addEventListener('click', () => {
    window.location.assign(redirectUrl || '/');
    document.body.removeChild(overlay);
  });

  buttonContainer.appendChild(button);
  modal.appendChild(buttonContainer);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}
